import React, { useEffect, useState } from "react";
import { Provider, useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import CAStore from "../../Store/TemplateStore/template.store";
import {
  cleanData,
  storeDigital,
  storeUserEmail,
  storePlantRowData,
} from "../../Store/TemplateStore/template.action";

import { getPlants, deletePlants } from "../../network/cipAPI";
import Header from "../../components/Header";
import AgTable from "../../components/AgTable";
import DupontDialog from "../../components/DupontDialog";
import DupontButton from "../../components/DupontButton";
import PageLoading from "../../components/PageLoading";
import createNotification, {
  SUCCESS_NOTIFICATION,
  ERROR_NOTIFICATION,
} from "../../components/Notifications";

import { updatePlantId } from "../../utils/Utils";
import { saveItemSession } from "../../utils/session";

import { PLANT_COL_DEF } from "./plantsTable.config";

import "./index.scss";
import { API_URLS } from "../../network/appUrls";
import ShareDialog from "../shareDialog";
import Footer from "../../common/footer";

const basePath = API_URLS.basePath;

export const store = CAStore;

const PlantPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useSelector((state) => state.token);
  const plantColDef = PLANT_COL_DEF(t);
  const [loadHome, setloadHome] = useState(false);
  const [cleaningAdvisorHome, setCleaningAdvisorHome] = useState(false);
  const [replacementAdvisorHome, setReplacementAdvisorHome] = useState(false);
  const [deletedRow, setDeletedRow] = useState(null);
  const [showDeletePopup, setShowDeletedDialog] = useState(false);
  const [isLoading, setisLoading] = useState(true);
  const [istermsDialogOpen, setIsTermsDialogOpen] = useState(false);
  const digital = useSelector((state) => state.digital);
  const [localDigital, setLocalDigital] = useState(digital);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const userEmail = useSelector((state) => state.userEmail);
  const [plantId, setPlantId] = useState(null);
  const plantRowData = useSelector((state) => state.plantRowData);
  const requestStatus = useSelector((state) => state.requestStatus);

  const handleTermsDialogOpen = () => {
    setIsTermsDialogOpen(true);
  };
  const handleTermsDialogClose = () => {
    setIsTermsDialogOpen(false);
    setLocalDigital(true);
    dispatch(storeDigital(true));
  };

  useEffect(() => {
    updatePlantId();
    setloadHome(true);
    dispatch(cleanData());
  }, []);

  useEffect(() => {
    setLocalDigital(digital);
  }, [digital]);

  useEffect(() => {
    dispatch(storeUserEmail(userEmail));
  }, [userEmail]);

  useEffect(() => {
    if (localDigital === "null" || localDigital === "false") {
      handleTermsDialogOpen();
    }
  }, [localDigital]);

  useEffect(() => {
    const getPlantDetails = async () => {
      try {
        const response = await getPlants();
        if (response && response.data) {
          dispatch(storePlantRowData(response.data));
          setisLoading(false);
        } else {
          dispatch(storePlantRowData([]));
        }
      } catch (err) {
      } finally {
        setisLoading(false);
      }
    };

    if (
      token &&
      (requestStatus === -1 ||
        requestStatus === 2 ||
        requestStatus === "-1" ||
        requestStatus === "2")
    ) {
      getPlantDetails();
    }
  }, [token, requestStatus]);

  const deleteHandler = async (params) => {
    setDeletedRow(params);
    setShowDeletedDialog(true);
  };

  const addNewPlant = () => {
    updatePlantId(0);
    const path = `${basePath}addPlant`;
    navigate(path);
  };

  const shareHandler = (params) => {
    updatePlantId(params.plant_id);
    setPlantId(params.plant_id);
    setShareDialogOpen(true);
  };

  const editHandler = (params) => {
    updatePlantId(params.plant_id);
    const path = `${basePath}editPlant?plantId=${
      params.plant_id
    }&isEdit=${true}`;
    navigate(path);
  };

  const viewHandler = (params) => {
    updatePlantId(params.plant_id);
    const path = `${basePath}home?plantId=${params.plant_id}`;
    navigate(path, { state: { isEdit: true } });
  };

  const onProceedClicked = async (value) => {
    setShowDeletedDialog(false);

    if (value === "CONFIRM") {
      const originalPlantRowData = [...plantRowData];

      try {
        const newPlantRowList = plantRowData.filter(
          (item) => item.plant_id !== deletedRow.plant_id
        );
        dispatch(storePlantRowData(newPlantRowList));

        // Show success toast early (optimistic UI)
        createNotification(
          SUCCESS_NOTIFICATION,
          t("notificationStrings.deleteSuccess"),
          ""
        );

        updatePlantId(deletedRow.plant_id);

        const res = await deletePlants();
        if (!res || res.status !== 200) {
          throw new Error("Delete failed due to bad response");
        }
      } catch (err) {
        dispatch(storePlantRowData(originalPlantRowData));

        createNotification(
          ERROR_NOTIFICATION,
          t("notificationStrings.deleteFail"),
          ""
        );
      }
    }
  };

  const navigateToAnalysisData = (data) => {
    updatePlantId(data.plant_id);
    const path = `${basePath}analyseData?plantId=${
      data.plant_id
    }&isEdit=${true}`;
    const plantPageURL = window.location.pathname + window.location.search;
    saveItemSession("water_type", data.water_type);
    saveItemSession("name", data.name);
    saveItemSession("plantPageURL", plantPageURL);
    navigate(path);
  };

  const handleClickOnPlantRow = (event) => {
    const clickedElement = event.event.target;
    if (clickedElement.closest(".custom-sharing-button")) {
      return;
    }
    const { data } = event;
    if (data.view === 1) editHandler(data);
    else if (data.view === 2) {
      navigateToAnalysisData(data);
    } else if (data.view === 3) {
      updatePlantId(data.plant_id);
      const path = `${basePath}home?plantId=${data.plant_id}`;
      navigate(path);
    }
  };

  return (
    <Provider store={store}>
      {loadHome && (
        <>
          <div className="container-main">
            <Header
              setCleaningAdvisorHome={setCleaningAdvisorHome}
              setReplacementAdvisorHome={setReplacementAdvisorHome}
            />
            <div className="main-container">
              <div className="content-container descriptionText">
                <div className="project-header-row">
                  <div className="heading-text ">{t("roAdvisorProject.roAdvisor")}</div>
                  <DupontButton
                    onClick={addNewPlant}
                    customClass="margin-left-10"
                    disabled={isLoading}
                  >
                    {t("roAdvisorProject.addPlant")}
                  </DupontButton>
                </div>
                <div>
                  {isLoading ? (
                    <PageLoading customHeight={"420px"} />
                  ) : (
                    plantRowData && (
                      <div>
                        <AgTable
                          columnDefs={plantColDef}
                          rowData={plantRowData}
                          hideExport
                          name={`${t("excelSheetNames.plants")}`}
                          context={{
                            deleteHandler,
                            editHandler,
                            viewHandler,
                            shareHandler,
                            userEmail,
                          }}
                          onRowClicked={handleClickOnPlantRow}
                        />
                        {showDeletePopup && (
                          <DupontDialog
                            open={showDeletePopup}
                            type="primary"
                            title={t(
                              "notificationStrings.deleteConfirmationProject"
                            )}
                            cancelBtnText={t("ValueStrings.no")}
                            confirmBtnText={t("ValueStrings.yes")}
                            onClose={onProceedClicked}
                          />
                        )}
                      </div>
                    )
                  )}
                </div>
              </div>
              {/* {istermsDialogOpen && (
              <TermsConditions
                istermsDialogOpen={istermsDialogOpen}
                handleTermsDialogClose={handleTermsDialogClose}
              />
            )} */}
              {shareDialogOpen && (
                <ShareDialog
                  setShareDialogOpen={setShareDialogOpen}
                  shareDialogOpen={shareDialogOpen}
                  plantId={plantId}
                />
              )}
              <Footer />
            </div>
          </div>
        </>
      )}
    </Provider>
  );
};

export default PlantPage;
